import { useCallback, useEffect, useSyncExternalStore } from "react";
import { useSelector } from "react-redux";
import {
  coerceAccessState,
  coerceState,
  getBillingOverview,
  getSeatUsage,
  isCustomerManaged as readCustomerManaged,
  type BillingAccessState,
  type BillingManagementNoticeView,
  type BillingOverviewView,
  type BillingSubscriptionState,
  type SeatUsageView,
} from "../api/Billing";

/**
 * Billing state for the self-serve portal and the global RestrictedBanner.
 *
 * Deliberately mirrors `useMySubscription.ts`: module-scope store behind
 * `useSyncExternalStore`, 60s stale window, refetch on focus, cache dropped when
 * the signed-in user changes, exported `refetch()`. Same reasoning applies —
 * MasterLayout mounts the banner, BillingIndex mounts several panels, and each
 * of those must read ONE snapshot rather than firing its own `/overview` call.
 *
 * ONE DIFFERENCE FROM `useMySubscription`, and it matters (collision C8):
 * `TierGuard` caches access state for 30s server-side and this hook holds it for
 * 60s client-side, which stacks to ~90s of "I paid and nothing happened". The
 * checkout return page therefore calls `refetch()` here AND on `useMySubscription`
 * the moment the poll reports `Completed`, bypassing both windows.
 *
 * FETCH GATE: this hook is mounted by MasterLayout for every authenticated user,
 * but `/api/Billing/*` is HR/Admin only and 403s everyone else — including
 * Operator, deliberately and with a defensive second check server-side. So the
 * hook takes an `enabled` flag and NEVER fires for a role that cannot read it.
 * Without that gate every Manager and Employee would generate a 403 pair on
 * every page load.
 */

const STALE_MS = 60_000;

export interface UseBillingResult {
  overview: BillingOverviewView | null;
  seats: SeatUsageView | null;
  isLoading: boolean;
  isError: boolean;
  refetch: () => Promise<void>;
  /** Commercial lifecycle. `Draft` until the first payment lands. */
  state: BillingSubscriptionState;
  /** Entitlement ladder. `Full` unless a payment has lapsed. */
  accessState: BillingAccessState;
  /** True once this tenant has a real self-serve subscription row. */
  isBillingManaged: boolean;
  /** Payment failed and the clock is running. Full function, loud banner. */
  isPastDue: boolean;
  /** Writes are blocked. Reads and exports are not, ever. */
  isRestricted: boolean;
  /**
   * Who may change this subscription, and if not the customer, who to contact. Always present on a
   * loaded overview from a current server; null while nothing has loaded.
   */
  management: BillingManagementNoticeView | null;
  /**
   * False when Mowazaf manages this subscription on the customer's behalf — the portal then renders
   * READ-ONLY: every figure, invoice, credit note and PDF stays visible, only the mutations go away.
   *
   * ⚠️ NOT `state === "Bespoke"`. That test was the client half of the central defect: a
   * subscription an operator marked paid or extended by hand is operator-managed while its state is
   * `Active`, and the old test offered it the full self-serve UI.
   */
  isCustomerManaged: boolean;
}

interface BillingStore {
  overview: BillingOverviewView | null;
  seats: SeatUsageView | null;
  isLoading: boolean;
  isError: boolean;
}

let store: BillingStore = { overview: null, seats: null, isLoading: false, isError: false };
let lastFetchedAt = 0;
let cachedUserId: string | undefined;
let inFlight = false;
const listeners = new Set<() => void>();

function getSnapshot(): BillingStore {
  return store;
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

function setStore(patch: Partial<BillingStore>): void {
  store = { ...store, ...patch };
  listeners.forEach((l) => l());
}

async function loadBilling(userId: string | undefined, force = false): Promise<void> {
  if (!userId) {
    cachedUserId = undefined;
    lastFetchedAt = 0;
    if (store.overview !== null || store.seats !== null || store.isLoading || store.isError) {
      setStore({ overview: null, seats: null, isLoading: false, isError: false });
    }
    return;
  }

  if (userId !== cachedUserId) {
    // Signed-in user changed. Drop the previous tenant's billing data before
    // anything renders — a stale seat count from another company on a money
    // surface is exactly the class of leak this codebase treats as critical.
    cachedUserId = userId;
    lastFetchedAt = 0;
    setStore({ overview: null, seats: null, isLoading: false, isError: false });
  } else if (!force && store.overview != null && Date.now() - lastFetchedAt < STALE_MS) {
    return;
  }

  if (inFlight) return;
  inFlight = true;
  setStore({ isLoading: store.overview == null, isError: false });
  try {
    const [overviewResult, seatsResult] = await Promise.allSettled([
      getBillingOverview(),
      getSeatUsage(),
    ]);

    let overview: BillingOverviewView | null = null;
    let seats: SeatUsageView | null = null;
    let failed = false;

    if (overviewResult.status === "fulfilled") {
      const r = overviewResult.value;
      if (r.status < 400 && r.errors.length === 0) overview = r.payload;
      else failed = true;
    } else {
      failed = true;
    }

    // Seats are supplementary. A company with no subscription yet legitimately
    // has no seat commitment, so a seats failure must not blank the overview.
    if (seatsResult.status === "fulfilled") {
      const r = seatsResult.value;
      if (r.status < 400 && r.errors.length === 0) seats = r.payload;
    }

    if (overview) {
      lastFetchedAt = Date.now();
      setStore({ overview, seats, isLoading: false, isError: false });
    } else {
      setStore({ isLoading: false, isError: failed });
    }
  } catch {
    setStore({ isLoading: false, isError: true });
  } finally {
    inFlight = false;
  }
}

/**
 * @param enabled pass `false` for any caller that is not HR/Admin of the owning
 *   company. Every other role gets a 403 from `/api/Billing/*` by design, and an
 *   Operator must never appear on the customer billing path at all.
 */
export function useBilling(enabled = true): UseBillingResult {
  const userId = useSelector((state: { user?: { userId?: string } }) => state.user?.userId);
  const effectiveUserId = enabled ? userId : undefined;

  const snapshot = useSyncExternalStore(subscribe, getSnapshot, getSnapshot);

  useEffect(() => {
    void loadBilling(effectiveUserId);
  }, [effectiveUserId]);

  useEffect(() => {
    if (!effectiveUserId) return;
    function onFocus() {
      if (Date.now() - lastFetchedAt >= STALE_MS) void loadBilling(effectiveUserId);
    }
    window.addEventListener("focus", onFocus);
    document.addEventListener("visibilitychange", onFocus);
    return () => {
      window.removeEventListener("focus", onFocus);
      document.removeEventListener("visibilitychange", onFocus);
    };
  }, [effectiveUserId]);

  const refetch = useCallback(() => loadBilling(effectiveUserId, true), [effectiveUserId]);

  const { overview, seats, isLoading, isError } = snapshot;
  const state = coerceState(overview?.state);
  const accessState = coerceAccessState(overview?.accessState);
  const isBillingManaged = !!overview?.isBillingManaged;
  const management = overview?.management ?? null;

  return {
    management,
    isCustomerManaged: readCustomerManaged(management),
    overview,
    seats,
    isLoading,
    isError,
    refetch,
    state,
    accessState,
    isBillingManaged,
    isPastDue: isBillingManaged && (state === "PastDue" || accessState === "Grace"),
    isRestricted:
      isBillingManaged && (accessState === "Restricted" || accessState === "Dormant"),
  };
}
